import React, { useCallback, useEffect, useState } from 'react';
import {
  Clear
} from "@mui/icons-material";
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Ability,
  Character,
  CharacterAbility,
  CharacterSpell,
  SpellsFromList,
  CharacterSheetInterface,
  CharacterExtraEffects,
  CharacterBasicItem,
  CreatureAbility,
  RolledValue,
  RollPlus,
  CharacterAction,
  StoredSpell,
  SpellsFromListWithCosts
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

import ViewSpell from "@dndComponents/display/ViewSpell";
import ToastBox from "@components/display/ToastBox";
import Roller from "@dndComponents/other/Roller";

interface Props {
  char: CharacterSheetInterface;
  obj: CharacterAbility | CharacterSpell | StoredSpell | CharacterExtraEffects;
  action: CharacterAction;
  simple?: boolean; // When this is true then it just shows a single button, and when clicked does a popover for the rest
  type?: string; // Spell or Spell As Ability or ""
  level?: number;
  ritual?: boolean;
  disabled: boolean;
  log_it?: boolean;
  shared: boolean;
  changed_costs?: { [resource: string]: number };
  // onChange: (changeTypes: string[]) => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterCastButton({
  char,
  obj,
  action,
  simple = false,
  type = "",
  level = -1,
  ritual = false,
  disabled: disabled_inherited,
  log_it = false,
  shared,
  changed_costs = {},
  // onChange,
}: Props) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [popoverTime, setPopoverTime] = useState(0);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [changing, setChanging] = useState(false);
  // const [change_types, setChangeTypes] = useState<string[]>([]);
  const [ping, setPing] = useState(false);

  const level2 = obj instanceof CharacterExtraEffects ? obj.slot_level : (level === -1 ? obj.level : level);

  const updateCharacter = useCallback(() => {
    char.changed();
    char.subscription_holder.resourcesChanged();
    char.subscription_holder.recalculate();
  }, [char]);

  useEffect(() => {
    // if (log_it) {
    //   console.log(change_types);
    // }
    if (changing) {
      // console.log("changing");
      // setChangeTypes([]);
      setChanging(false);
      setTimeout(() => {
        updateCharacter();
      }, 1000);
    }
  }, [changing, log_it, updateCharacter]);
    
  const renderCastOptions = () => {
    // debugger;
    let disabled = disabled_inherited;
    if (obj instanceof CharacterSpell) {
      if (obj.spell) {
        if ((obj.level === 0 || obj.at_will) && obj.spell.concentration && char.body.concentrating_on) {
          return (
            <div>
              You're already concentrating on 
              { (char.body.concentrating_on instanceof CharacterSpell || char.body.concentrating_on instanceof CharacterAbility) &&
                <ViewSpell spell={char.body.concentrating_on} show_ritual />
              }
              <DandDButtonBox 
                name={ `${obj.level < level2 && obj.level !== 0 ? "Upc" : "C"}ast at Level ${level2} Anyway` }
                disabled={ disabled }
                onClick={() => {
                  triggerAbilityEffect("Slots");
                }} 
              />
            </div>
          );
        } else if (obj.ritual_only) {
          return null;
        } else {
          // Get the slots for the level
          const level_slots = char.body.use_slots.filter(o => o.level === level2);
          const unused_slots = level_slots.filter(o => o.used < o.total);
          if (unused_slots.length === 1) {
            const char_slot = unused_slots[0];
            if (obj.spell.concentration && char.body.concentrating_on) {
              return (
                <div>
                  You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                  <DandDButtonBox name={ `${obj.level < level2 && obj.level !== 0 ? "Upc" : "C"}ast at Level ${level2} Anyway` }
                    disabled={ disabled || char_slot.used === char_slot.total }
                    onClick={() => {
                      triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                    }} 
                  />
                </div>
              );
            }
          } else {
            if (obj.spell.concentration && char.body.concentrating_on) {
              return (
                <div>
                  You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                  <div>
                    { unused_slots.map((char_slot) => {
                      return (
                        <DandDButtonBox key={char_slot.resource_true_id} name={char_slot.slot_name}
                          disabled={ disabled || char_slot.used === char_slot.total }
                          onClick={() => {
                            triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                          }} 
                        />
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              return (
                <div>
                  { unused_slots.map((char_slot, key) => {
                    return (
                      <DandDButtonBox key={key} name={char_slot.slot_name}
                        disabled={ disabled || char_slot.used === char_slot.total }
                        onClick={() => {
                          triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                        }} 
                      />
                    );
                  })}
                </div>
              );
            }
          }
        }
      } else {
        return null;
      }
    } else if (obj instanceof StoredSpell) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <DandDButtonBox name={ obj.castings === 1 ? (obj.has_been_cast ? "Stored Spell has been Cast" : `Cast Stored Spell`) : `${obj.has_been_cast}/${obj.castings} Castings of Stored Spell` }
              disabled={ disabled || obj.has_been_cast >= obj.castings } // obj.disabled({char: char, action, level: level2}) }
              onClick={() => {
                triggerAbilityEffect("Stored Spell");
              }} 
            />
          </Grid>
          { obj.has_been_cast >= obj.castings && 
            <Grid size={12}>
              <DandDButtonBox name="Remove Stored Spell"
                disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                onClick={() => {
                  if (char instanceof Character) {
                    const char_item = char.inventory.items.find(o => o.true_id === obj.item_id);
                    if (char_item) {
                      if (char_item.spell_storing_spell.true_id === obj.true_id) {
                        char_item.spell_storing_spell = new StoredSpell();
                      } else {
                        char_item.stored_spells = char_item.stored_spells.filter(o => o.true_id !== obj.true_id);
                      }
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      char.subscription_holder.recalculate();
                    }
                  }
                }} 
              />
            </Grid>
          }
        </Grid>
      );
    } else if (obj instanceof CharacterAbility) {
      const the_ability = obj.the_ability;
      
      // if (action.name.includes("Fire")) {
      //   console.log(action);
      // }
      if (the_ability || obj.sidekick_ability) {
        let { resource_consumed, amount_consumed, slot, disabled: action_disabled } = char_util.getResourceStuff({ char: char, action, obj });
        disabled = disabled || action_disabled;
        if (slot) {
          if (resource_consumed !== "Pact") {
            resource_consumed = "Slot";
          }
        } else if (!["Charge","None"].includes(resource_consumed)) {
          resource_consumed = "Special";
        }
        if (resource_consumed === "Slot" || resource_consumed === "Pact") {
          // Get the slots for the level
          const level_slots = resource_consumed === "Pact" ? char.body.use_slots.filter(o => o.slot_name.includes("Pact") && o.level === level2) : char.body.use_slots.filter(o => o.level === level2);
          const unused_slots = level_slots.filter(o => o.used < o.total);
          if (unused_slots.length === 1) {
            const char_slot = unused_slots[0];
            if (obj.concentration && char.body.concentrating_on) {
              return (
                <div>
                  You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                  <DandDButtonBox name={`Cast with ${char_slot.slot_name} Anyway`}
                    disabled={ disabled } // char_slot.used + +amount_consumed > char_slot.total
                    onClick={() => {
                      triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                    }} 
                  />
                </div>
              );
            } else {
              // It will never go here because then it wouldn't have done cast options
              return (<span>I didn't think it could go here</span>);
            }
          } else {
            if (obj.concentration && char.body.concentrating_on) {
              return (
                <div>
                  You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                  <div>
                    { unused_slots.map((char_slot, key) => {
                      return (
                        <DandDButtonBox key={key} name={char_slot.slot_name}
                          disabled={ disabled } // char_slot.used + +amount_consumed > char_slot.total
                          onClick={() => {
                            triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                          }} 
                        />
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              return (
                <div>
                  { unused_slots.map((char_slot, key) => {
                    return (
                      <DandDButtonBox key={key} name={char_slot.slot_name}
                        disabled={ disabled } // char_slot.used + +amount_consumed > char_slot.total
                        onClick={() => {
                          triggerAbilityEffect(char_slot.type_id, char_slot.resource_true_id);
                        }} 
                      />
                    );
                  })}
                </div>
              );
            }
          }
        } else if (the_ability instanceof Ability && resource_consumed === "Charge" && char instanceof Character) {
          // obj.source_id; // This is the true_id of the CharacterItem that grants this ability
          const item_id = obj.char_feature?.use_item_id ?? "";
          const item_finder = char.inventory.items.filter(o => o.true_id === item_id);
          if (item_finder.length === 1) {
            const char_item = item_finder[0];
            return (
              <DandDButtonBox name={ `${amount_consumed} Charge${amount_consumed > 1 ? "s" : ""}|${char_item.charges_used}/${char_item.max_charges}` }
                disabled={ disabled } // (char_item.charges_used + obj.special_resource_amount) > char_item.max_charges }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  triggerAbilityEffect("Charge");
                }} 
              />
            );
          }
        } else if (resource_consumed === "Special") {
          if (obj.concentration && char.body.concentrating_on) {
            return (
              <div>
                You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                <DandDButtonBox name="Use Anyway"
                  disabled={ disabled } // char.body.resource_holder.getSpecialResourceUsed(obj.true_id) + +amount_consumed > obj.special_resource_amount }
                  onClick={() => {
                    triggerAbilityEffect("Slots");
                  }} 
                />
              </div>
            );
          } else {
            // It will never go here because then it wouldn't have done cast options
            return (<span>I didn't think it could go here</span>);
          }
        } else if (resource_consumed === "None") {
          if (char.body.concentrating_on) {
            return (
              <div>
                You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                <DandDButtonBox name="Use Anyway"
                  disabled={ disabled } // (char.body.resource_holder.getSpecialResourceUsed(obj.true_id) + +amount_consumed) > obj.special_resource_amount }
                  onClick={() => {
                    triggerAbilityEffect("Slots");
                  }} 
                />
              </div>
            );
          } else {
            return (<span>I didn't think it could go here</span>);
          }
        } else if (resource_consumed === "Recharge") {
          console.error("Shouldn't come here");
        } else {
          // Get the resources
          const resources = char.body.resources.filter(o => o.type_id === resource_consumed);
          
          if (resources.length > 0) {
            // let used = 0;
            // let total = 0;
            // resources.forEach(o => {
            //   // used += o.used;
            //   total += o.total;
            // });
            if (obj.concentration && char.body.concentrating_on) {
              return (
                <div>
                  You're already concentrating on <ViewSpell spell={char.body.concentrating_on} show_ritual />
                  <DandDButtonBox name="Use Anyway"
                    disabled={ disabled } // (used + +amount_consumed) > total }
                    onClick={() => {
                      triggerAbilityEffect("Slots");
                    }} 
                  />
                </div>
              );
            } else {
              // It will never go here because then it wouldn't have done cast options
              return (<span>I didn't think it could go here</span>);
            }
          }
        }
      }
      return null;
    }
  }

  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderCastOptions() }
        </Popover>
        <ToastBox
          openTime={ popoverTime }
        >
          { renderRoll() }
        </ToastBox>
      </>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name="Recharge"
        rolled_values={roll} 
        type="Recharge" 
      />
    );
  }

  const getSlotType = (resource_consumed: string) => {
    let slot_type = "Slots";
    if (resource_consumed === "Slots") {
      // Just get whatever slots there are at that level.
      // Take created first, then pact, then regular
      const slot_finder = char.body.use_slots.filter(o => o.level === level2);
      if (slot_finder.length > 1) {
        let slot_finder2 = slot_finder.filter(o => o.type_id === "Created (Pact)");
        if (slot_finder2.length === 1) {
          slot_type = "Created (Pact)";
        } else {
          slot_finder2 = slot_finder.filter(o => o.slot_name === "Pact");
          if (slot_finder2.length === 1) {
            slot_type = slot_finder[0].type_id;
          } else {
            slot_finder2 = slot_finder.filter(o => o.type_id === "Created");
            if (slot_finder2.length === 1) {
              slot_type = "Created";
            } else {
              slot_type = slot_finder2[0].type_id;
            }
          }
        }
      } else {
        slot_type = slot_finder[0].type_id;
      }
    } else if (resource_consumed === "Pact") {
      // console.log(level);
      // console.log(obj);
      // console.log(level2);
      // console.log(character.body.use_slots);
      // Get the actual Pact slot id
      const slot_finder = char.body.use_slots.filter(o => o.level === level2 && 
        o.slot_name.includes("Pact"));
      if (slot_finder.length > 1) {
        let slot_finder2 = slot_finder.filter(o => o.type_id === "Created (Pact)");
        if (slot_finder2.length === 1) {
          slot_type = "Created (Pact)";
        } else {
          slot_finder2 = slot_finder.filter(o => o.slot_name === "Pact");
          if (slot_finder2.length === 1) {
            slot_type = slot_finder[0].type_id;
          }
        }
      } else {
        slot_type = slot_finder[0].type_id;
      }
    } else {
      slot_type = resource_consumed;
    }
    return slot_type;
  }

  const doit = true;
  const triggerAbilityEffect = (slot_type: string, resource_true_id = "") => {
    // debugger;
    if (obj instanceof CharacterAbility) {
      const the_ability = obj.the_ability;
      if (the_ability || obj.sidekick_ability) {
        const { resource_consumed, amount_consumed, slot, and_special } = char_util.getResourceStuff({ char: char, action, obj, slot_type });
        let go = false;
        const new_change_types: string[] = [];
        if (the_ability instanceof SpellsFromList) {
          if (resource_consumed === "Special") {
            const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
            if (special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
              char.body.resource_holder.setSpecialResourceUsed(obj.true_id, special_resource_used +amount_consumed);
              go = true;
              new_change_types.push("hp");
            }
          } else if (resource_consumed === "Charge" && char instanceof Character) {
            // debugger;
            const item_id = obj.item_id;
            const char_item = char.inventory.items.filter(o => o.true_id === item_id)[0];
            if (char_item) {
              if ((char_item.item_type === "Potion" || char_item.item_type === "Scroll" || char_item.item_type === "Dust") && char_item instanceof CharacterBasicItem) {
                char_item._count -= 1;
              } else {
                char_item.charges_used += +amount_consumed;
              }
              go = true;
              new_change_types.push("hp");
            }
          } else if (and_special) {
            slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
            let slots = char.body.use_slots.filter(o => o.level === level2 && 
              (o.type_id === slot_type || o.slot_name.includes(slot_type)));
            if (slots.length > 1) {
              slots = slots.filter(o => o.resource_true_id === resource_true_id);
            }
                
            if (slots.length === 1) {
              const char_slot = slots[0];

              if (char_slot.used + 1 <= char_slot.total) {
                if (the_ability.resource_consumed === "Special") {
                  const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
                  if (special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
                    char.body.resource_holder.setSpecialResourceUsed(obj.true_id, special_resource_used +amount_consumed);
                    char_slot.used++;
                    if (char_slot.is_resource) {
                      const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                      if (char_resource) {
                        char_resource.used = char_slot.used;
                      }
                    }
                    go = true;
                    new_change_types.push("Resources");
                  }
                } else {
                  const resources = char.body.resources.filter(o => o.type_id === the_ability.resource_consumed);
                    
                  if (resources.length === 1) {
                    const resource = resources[0];
                    if (resource.used + +amount_consumed <= resource.total) {
                      resource.used += +amount_consumed;
                      char_slot.used++;
                      if (char_slot.is_resource) {
                        const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                        if (char_resource) {
                          char_resource.used = char_slot.used
                        }
                      }
                      go = true;
                      new_change_types.push("Resources");
                    }
                  }
                }
              }
            }
          } else if (slot) {
            slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
            let slots = char.body.use_slots.filter(o => o.level === level2 && 
              (o.type_id === slot_type || o.slot_name.includes(slot_type)));
            if (slots.length > 1) {
              slots = slots.filter(o => o.resource_true_id === resource_true_id);
            }
                
            if (slots.length === 1) {
              const char_slot = slots[0];
              if (char_slot.used + +amount_consumed <= char_slot.total) {
                char_slot.used += +amount_consumed;
                if (char_slot.is_resource) {
                  const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                  if (char_resource) {
                    char_resource.used = char_slot.used
                  }
                }
                go = true;
                new_change_types.push("Resources");
              }
            }
          } else {
            // debugger;
            if (resource_consumed === "Slots" || resource_consumed === "Pact") {
              slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
              let slots = char.body.use_slots.filter(o => o.level === level2 && 
                (o.type_id === slot_type || o.slot_name.includes(slot_type)));
              if (slots.length > 1) {
                slots = slots.filter(o => o.resource_true_id === resource_true_id);
              }
              
              if (slots.length === 1) {
                const char_slot = slots[0];
                if (char_slot.used + +amount_consumed <= char_slot.total) {
                  char_slot.used += +amount_consumed;
                  if (char_slot.is_resource) {
                    const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                    if (char_resource) {
                      char_resource.used = char_slot.used
                    }
                  }
                  go = true;
                  new_change_types.push("Resources");
                }
              }
            } else if (resource_consumed === "") {
              go = true;
              new_change_types.push("Resources");
            } else {
              const resources = char.body.resources.filter(o => o.type_id === resource_consumed);
                  
              if (resources.length === 1) {
                const resource = resources[0];
                if (resource.used + +amount_consumed <= resource.total) {
                  resource.used += +amount_consumed;
                  go = true;
                  new_change_types.push("Resources");
                }
              }
            }
          }
          if (false) {
            // if (the_ability.slot_override === "Don't Override") {
            //   resource_consumed = "Slot";
            //   amount_consumed = 1;
            // } else if (the_ability.slot_override === "Or Special Resource") {
            //   done = true;

            //   if (slot_type === "Special") {
            //     if (obj.special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
            //       obj.special_resource_used += +amount_consumed;
            //       go = true;
            //       change_types.push("Resources");
            //     }
            //   } else if (slot_type === "Charge") {
            //     console.log("Not implemented");
            //   } else {
            //     if (slot_type === "Slots") {
            //       // Just get whatever slots there are at that level.
            //       // Take created first, then pact, then regular
            //       let slot_finder = character.body.use_slots.filter(o => o.level === level);
            //       if (slot_finder.length > 1) {
            //         let slot_finder2 = slot_finder.filter(o => o.type_id === "Created (Pact)");
            //         if (slot_finder2.length === 1) {
            //           slot_type = "Created (Pact)";
            //         } else {
            //           slot_finder2 = slot_finder.filter(o => o.slot_name === "Pact");
            //           if (slot_finder2.length === 1) {
            //             slot_type = slot_finder[0].type_id;
            //           } else {
            //             slot_finder2 = slot_finder.filter(o => o.type_id === "Created");
            //             if (slot_finder2.length === 1) {
            //               slot_type = "Created";
            //             } else {
            //               slot_type = slot_finder2[0].type_id;
            //             }
            //           }
            //         }
            //       } else {
            //         slot_type = slot_finder[0].type_id;
            //       }
            //     }
            //     const slots = character.body.use_slots.filter(o => o.level === level && o.type_id === slot_type);
                    
            //     if (slots.length === 1) {
            //       const slot = slots[0];
            //       if (slot.used + +amount_consumed <= slot.total) {
            //         slot.used += +amount_consumed;
            //         go = true;
            //         change_types.push("Resources");
            //       }
            //     }
            //   }
            // }
          }
        } else if (obj.sidekick_ability) {
          if (resource_consumed === "Special") {
            const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
            if (special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
              char.body.resource_holder.setSpecialResourceUsed(obj.true_id, special_resource_used +amount_consumed);
              go = true;
              new_change_types.push("Resources");
            }
          } else if (slot) {
            slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
            let slots = char.body.use_slots.filter(o => o.level === level2 && 
              (o.type_id === slot_type || o.slot_name.includes(slot_type)));
            if (slots.length > 1) {
              slots = slots.filter(o => o.resource_true_id === resource_true_id);
            }
                
            if (slots.length === 1) {
              const char_slot = slots[0];
              if (char_slot.used + +amount_consumed <= char_slot.total) {
                char_slot.used += +amount_consumed;
                if (char_slot.is_resource) {
                  const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                  if (char_resource) {
                    char_resource.used = char_slot.used
                  }
                }
                go = true;
                new_change_types.push("Resources");
              }
            }
          } else {
            // debugger;
            if (resource_consumed === "Slots") {
              slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
              let slots = char.body.use_slots.filter(o => o.level === level2 && 
                (o.type_id === slot_type || o.slot_name.includes(slot_type)));
              if (slots.length > 1) {
                slots = slots.filter(o => o.resource_true_id === resource_true_id);
              }
              
              if (slots.length === 1) {
                const char_slot = slots[0];
                if (char_slot.used + +amount_consumed <= char_slot.total) {
                  char_slot.used += +amount_consumed;
                  if (char_slot.is_resource) {
                    const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                    if (char_resource) {
                      char_resource.used = char_slot.used
                    }
                  }
                  go = true;
                  new_change_types.push("Resources");
                }
              }
            } else if (resource_consumed === "") {
              go = true;
              new_change_types.push("Resources");
            } else {
              const resources = char.body.resources.filter(o => o.type_id === resource_consumed);
                  
              if (resources.length === 1) {
                const resource = resources[0];
                if (resource.used + +amount_consumed <= resource.total) {
                  resource.used += +amount_consumed;
                  go = true;
                  new_change_types.push("Resources");
                }
              }
            }
          }
        } else {
          if (resource_consumed === "Slot" || resource_consumed === "Pact") {
            slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
            const slots = char.body.use_slots.filter(o => o.level === level2 && 
              (o.type_id === slot_type || o.slot_name === slot_type));
                
            if (slots.length === 1) {
              const char_slot = slots[0];
              if (char_slot.used + +amount_consumed <= char_slot.total) {
                char_slot.used += +amount_consumed;
                if (char_slot.is_resource) {
                  const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                  if (char_resource) {
                    char_resource.used = char_slot.used
                  }
                }
                go = true;
                new_change_types.push("Resources");
              }
            }
          } else if (resource_consumed === "Charge" && char instanceof Character) {
            const item_id = obj.item_id;
            const char_item = char.inventory.items.filter(o => o.true_id === item_id)[0];
            if (char_item) {
              if ((char_item.item_type === "Potion" || char_item.item_type === "Scroll" || char_item.item_type === "Dust") && char_item instanceof CharacterBasicItem) {
                char_item._count -= 1;
              } else {
                char_item.charges_used += +amount_consumed;
              }
              go = true;
              new_change_types.push("Resources");
            }
          } else if (resource_consumed === "Special") {
            const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
            if (special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
              char.body.resource_holder.setSpecialResourceUsed(obj.true_id, special_resource_used +amount_consumed);
              go = true;
              new_change_types.push("Resources");
            }
          } else if (resource_consumed === "Recharge") {
            const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
            if (special_resource_used === 0) {
              char.body.resource_holder.setSpecialResourceUsed(obj.true_id, 1);
              go = true;
              new_change_types.push("Resources");
            }
          } else if (resource_consumed === "None") {
            go = true;
          } else {
            const resources = char.body.resources.filter(o => o.type_id === resource_consumed);
            
            if (resources.length > 0) {
              let used = 0;
              let total = 0;
              resources.forEach(o => {
                used += o.used;
                total += o.total;
              });
              let to_add = +amount_consumed;
              if (used + to_add <= total) {
                go = true;
                new_change_types.push("Resources");
                let i = 0;
                while (i < resources.length && to_add > 0) {
                  const resource = resources[i];
                  if (to_add < (resource.total - resource.used)) {
                    resource.used += to_add;
                  } else {
                    to_add -= resource.total - resource.used;
                    resource.used = resource.total;
                  }
                  i++;
                }
              }
            }
          }
        }
        if (go) {
          if (obj.concentration) {
            // Set the concentration
            char.body.concentrating_on = obj;
            new_change_types.push("Concentration");
          }
          // setChangeTypes(new_change_types);
          setChanging(true);
        }
      }
    } else if (obj instanceof CharacterExtraEffects) {
      const the_ability = obj.main_cf;
      if (the_ability) {
        const { resource_consumed, amount_consumed } = char_util.getResourceStuff({ char: char, action, obj, slot_type });
        let go = false;
        const new_change_types: string[] = [];
        if (resource_consumed === "Slot" || resource_consumed === "Pact") {
          slot_type = slot_type === "Slots" ? getSlotType(resource_consumed) : slot_type;
          const slots = char.body.use_slots.filter(o => o.level === level2 && 
            (o.type_id === slot_type || o.slot_name === slot_type));
              
          if (slots.length === 1) {
            const char_slot = slots[0];
            if (char_slot.used + +amount_consumed <= char_slot.total) {
              char_slot.used += +amount_consumed;
              if (char_slot.is_resource) {
                const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
                if (char_resource) {
                  char_resource.used = char_slot.used
                }
              }
              go = true;
              new_change_types.push("Resources");
            }
          }
        } else if (resource_consumed === "Charge" && char instanceof Character) {
          const item_id = obj.item_id;
          const char_item = char.inventory.items.filter(o => o.true_id === item_id)[0];
          if (char_item) {
            if ((char_item.item_type === "Potion" || char_item.item_type === "Scroll" || char_item.item_type === "Dust") && char_item instanceof CharacterBasicItem) {
              char_item._count -= 1;
            } else {
              char_item.charges_used += +amount_consumed;
            }
            go = true;
            new_change_types.push("Resources");
          }
        } else if (resource_consumed === "Special") {
          const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
          if (special_resource_used + +amount_consumed <= +obj.special_resource_amount) {
            char.body.resource_holder.setSpecialResourceUsed(obj.true_id, special_resource_used +amount_consumed);
            go = true;
            new_change_types.push("Resources");
          }
        } else {
          const resources = char.body.resources.filter(o => o.type_id === resource_consumed);
          
          if (resources.length > 0) {
            let used = 0;
            let total = 0;
            resources.forEach(o => {
              used += o.used;
              total += o.total;
            });
            let to_add = +amount_consumed;
            if (used + to_add <= total) {
              go = true;
              new_change_types.push("Resources");
              let i = 0;
              while (i < resources.length && to_add > 0) {
                const resource = resources[i];
                if (to_add < (resource.total - resource.used)) {
                  resource.used += to_add;
                } else {
                  to_add -= resource.total - resource.used;
                  resource.used = resource.total;
                }
                i++;
              }
            }
          }
        }
        if (go) {
          // setChangeTypes(new_change_types);
          setChanging(true);
        }
      }
    } else if (obj instanceof CharacterSpell) {
      if (doit) {
        const new_change_types: string[] = [];
        const spell = obj;
        if (true) {
          let slots = char.body.use_slots.filter(o => o.level === level2 && 
            (o.type_id === slot_type || o.slot_name === slot_type));
          if (slots.length > 1) {
            slots = slots.filter(o => o.resource_true_id === resource_true_id);
          }
          if (slots.length === 1) {
            const char_slot = slots[0];
            char_slot.used++;
            if (char_slot.is_resource) {
              const char_resource = char.body.resources.find(o => o.type_id === char_slot.type_id);
              if (char_resource) {
                char_resource.used = char_slot.used
              }
            }
            new_change_types.push("Resources");
          }
        }
        if (spell.concentration) {
          char.body.concentrating_on = spell;
          new_change_types.push("Concentration");
        }
        // setChangeTypes(new_change_types);
        setChanging(true);
      }
    } else if (obj instanceof StoredSpell && char instanceof Character) {
      if (doit) {
        // debugger;
        const char_item = char.inventory.items.find(o => o.true_id === obj.item_id);
        if (char_item) {
          let ss = char_item.stored_spells.find(o => o.true_id === obj.true_id);
          if (!ss && char_item.spell_storing_spell.true_id === obj.true_id) {
            ss = char_item.spell_storing_spell;
          }
          if (ss) {
            const new_change_types: string[] = [];
            const spell = ss;
            spell.has_been_cast++;
            new_change_types.push("Resources");
            if (spell.concentration) {
              char.body.concentrating_on = spell;
              new_change_types.push("Concentration");
            }
            char.changed();
            char.subscription_holder.itemsChanged();
            // setChangeTypes(new_change_types);
            setChanging(true);
            setPing(!ping);
          }
        }
      }
    }
    setPopoverAnchorEl(null);
  }

  if (log_it) {
    console.log("Rendering");
  }
  // if (obj.name.includes("Divine Smite") && !action.special_resource) {
  //   debugger;
  // }
  // if (obj.name.includes("Cultists")) {
  //   debugger;
  // }
  if (obj instanceof CharacterAbility) {
    const the_ability = obj.the_ability;
        
    if (the_ability || obj.sidekick_ability) {
      if (ritual) {
        return (
          <DandDButtonBox 
            name="Ritual" 
            disabled={true} 
            onClick={() => {
            }}
          />
        );
      } else {
        if (log_it) {
          debugger;
        }
        let { resource_consumed, amount_consumed, slot, disabled: action_disabled } = char_util.getResourceStuff({ char: char, action, obj });
        let disabled = disabled_inherited || action_disabled;
        if (slot) {
          if (resource_consumed !== "Pact") {
            resource_consumed = "Slot";
          }
        } 
        // else if (!["Charge","None"].includes(resource_consumed)) {
        //   resource_consumed = "Special"; // This makes it so all others are counted as Special. Not sure that's necessary.
        // }
        if (resource_consumed === "Slot" || resource_consumed === "Pact" || type === "Spell") {
          if (level2 === 0) {
            if (the_ability instanceof SpellsFromList ||
              the_ability instanceof SpellsFromListWithCosts) {
              if (obj.the_spell && obj.concentration) {
                if (char.body.concentrating_on) {
                  return (
                    <>
                      <DandDButtonBox name="At Will"
                        disabled={ disabled }
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                          setPopoverAnchorEl(event.currentTarget);
                        }} 
                      />
                      { renderExtras() }
                    </>
                  );
                } else {
                  return (
                    <DandDButtonBox name="At Will"
                      disabled={ disabled }
                      onClick={() => {
                        triggerAbilityEffect("Slots");
                      }} 
                    />
                  );
                }
              } else {
                return (
                  <DandDButtonBox 
                    name="At Will"
                    disabled={disabled}
                    onClick={() => {
                      triggerAbilityEffect("Slots");
                    }}
                  />
                );
              }
            } else {
              return (
                <DandDButtonBox name="At Will"
                  disabled={disabled}
                  onClick={() => {
                    triggerAbilityEffect(resource_consumed);
                  }} 
                />
              );
            }
          } else {
            // Get the slots for the level
            const level_slots = char.body.use_slots.filter(o => o.level === level2);
            const unused_slots = level_slots.filter(o => o.used < o.total);
            if (unused_slots.length === 1) {
              const slot = unused_slots[0];
              if (obj.concentration && char.body.concentrating_on) {
                if (simple) {
                  return (
                    <>
                      <DandDButtonBox name={ `${obj.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                        // log_it={obj.name === "Misty Hand"}
                        disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                          setPopoverAnchorEl(event.currentTarget);
                        }} 
                      />
                      { renderExtras() }
                    </>
                  );
                } else {
                  return (
                    <div>You're already concentrating on
                      { char.body.concentrating_on &&
                        <Grid container spacing={0} direction="row">
                          <Grid
                            size={{
                              xs: 9
                            }}>
                            <ViewSpell spell={char.body.concentrating_on} show_ritual />
                          </Grid>
                          <Grid
                            size={{
                              xs: 3
                            }}>
                            { !disabled && 
                              <Clear 
                                style={{ cursor: "pointer" }} 
                                onClick={() => {
                                  char.body.concentrating_on = null;
                                  // setChangeTypes(["Concentration"]);
                                  setChanging(true);
                                }} 
                              />
                            }
                          </Grid>
                        </Grid> 
                      }
                      <DandDButtonBox name="Use Anyway"
                        disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                        onClick={() => {
                          triggerAbilityEffect(slot.type_id);
                        }} 
                      />
                    </div>
                  );
                }
              } else {
                if (the_ability instanceof Ability) {
                  return (
                    <DandDButtonBox name={ `Use with Level ${level2} Slot` }
                      disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                      onClick={() => {
                        triggerAbilityEffect(slot.type_id);
                      }} 
                    />
                  );
                } else {
                  return (
                    <DandDButtonBox name={ `${obj.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                      disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                      onClick={() => {
                        triggerAbilityEffect(slot.type_id);
                      }} 
                    />
                  );
                }
              }
            } else if (unused_slots.length > 0) {
              if (simple) {
                return (
                  <>
                    <DandDButtonBox name={ type === "Spell" ? (`${obj.level < level2 ? "Upc" : "C"}ast at Level ${level2}`) : obj.use_string({amount_consumed, obj: char, action})}
                      disabled={ disabled }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        setPopoverAnchorEl(event.currentTarget);
                      }} 
                    />
                    { renderExtras() }
                  </>
                );
              } else if (obj.concentration && char.body.concentrating_on) {
                return (
                  <div>You're already concentrating on
                    { char.body.concentrating_on &&
                      <Grid container spacing={0} direction="row">
                        <Grid
                          size={{
                            xs: 9
                          }}>
                          <ViewSpell spell={char.body.concentrating_on} show_ritual />
                        </Grid>
                        <Grid
                          size={{
                            xs: 3
                          }}>
                          { !disabled && 
                            <Clear 
                              style={{ cursor: "pointer" }} 
                              onClick={() => {
                                char.body.concentrating_on = null;
                                // setChangeTypes(["Concentration"]);
                                setChanging(true);
                              }} 
                            />
                          }
                        </Grid>
                      </Grid> 
                    }
                    <div>
                      { unused_slots.map((slot, key) => {
                        return (
                          <DandDButtonBox key={key} name={slot.slot_name}
                            disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                            onClick={() => {
                              triggerAbilityEffect(slot.type_id);
                            }} 
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div>
                    { unused_slots.map((slot, key) => {
                      return (
                        <DandDButtonBox key={key} name={slot.slot_name}
                          disabled={ disabled } // obj.disabled({char: char, action, level: level2, type: slot.type_id }) }
                          onClick={() => {
                            triggerAbilityEffect(slot.type_id);
                          }} 
                        />
                      );
                    })}
                  </div>
                );
              }
            } else {
              // Multiple slots of level, but all used. Show, but disabled
              return (
                <>
                  <DandDButtonBox name={ type === "Spell" ? (`${obj.level < level2 ? "Upc" : "C"}ast at Level ${level2}`) : obj.use_string({amount_consumed, obj: char, action})}
                    disabled
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    }} 
                  />
                  { renderExtras() }
                </>
              );
            }
          }
        } else if ((the_ability instanceof Ability || 
          the_ability instanceof SpellsFromList ||
          the_ability instanceof SpellsFromListWithCosts
          ) && resource_consumed === "Charge" && char instanceof Character) {
          // obj.source_id; // This is the true_id of the CharacterItem that grants this ability
          const item_id = obj.char_feature?.use_item_id ?? "";
          const item_finder = char.inventory.items.find(o => o.true_id === item_id);
          if (item_finder) {
            const char_item = item_finder;
            if (char_item.item_type === "Potion" || char_item.item_type === "Scroll" || char_item.item_type === "Dust") {
              // if (obj.name.includes("Holy Water")) {
              //   debugger;
              // }
              return (
                <DandDButtonBox name={ `${char_item.count} in Inventory` }
                  disabled={ disabled } // char_item.count <= 0 }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    triggerAbilityEffect("Charge");
                  }} 
                />
              );
            } else {
              return (
                <DandDButtonBox name={ `${amount_consumed} Charge${amount_consumed > 1 ? "s" : ""}|${char_item.charges_used}/${char_item.max_charges}` }
                  disabled={ disabled } // (char_item.charges_used + amount_consumed) > char_item.max_charges }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    triggerAbilityEffect("Charge");
                  }} 
                />
              );
            }
          }
        } else if (resource_consumed === "At Will") {
          if (obj.concentration && char.body.concentrating_on) {
            if (simple) {
              return (
                <>
                  <DandDButtonBox name="At Will"
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      setPopoverAnchorEl(event.currentTarget);
                    }} 
                  />
                  { renderExtras() }
                </>
              );
            } else {
              return (
                <div>You're already concentrating on
                  { char.body.concentrating_on &&
                    <Grid container spacing={0} direction="row">
                      <Grid size={9}>
                        <ViewSpell spell={char.body.concentrating_on} show_ritual />
                      </Grid>
                      <Grid size={3}>
                        { !disabled &&
                          <Clear 
                            style={{ cursor: "pointer" }} 
                            onClick={() => {
                              char.body.concentrating_on = null;
                              // setChangeTypes(["Concentration"]);
                              setChanging(true);
                            }} 
                          />
                        }
                      </Grid>
                    </Grid> 
                  }
                  <DandDButtonBox name="Use Anyway"
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={() => {
                      triggerAbilityEffect("At Will");
                    }} 
                  />
                </div>
              );
            }
          } else {
            return (
              <DandDButtonBox name="At Will"
                disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                onClick={() => {
                  if (obj.concentration) {
                    char.body.concentrating_on = obj;
                  }
                  triggerAbilityEffect("At Will");
                }} 
              />
            );
          }
        } else if (resource_consumed === "Special") {
          if (obj.concentration && char.body.concentrating_on) {
            if (simple) {
              return (
                <>
                  <DandDButtonBox name={obj.use_string({amount_consumed, obj: char, action})}
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      setPopoverAnchorEl(event.currentTarget);
                    }} 
                  />
                  { renderExtras() }
                </>
              );
            } else {
              return (
                <div>You're already concentrating on
                  { char.body.concentrating_on &&
                    <Grid container spacing={0} direction="row">
                      <Grid size={9}>
                        <ViewSpell spell={char.body.concentrating_on} show_ritual />
                      </Grid>
                      <Grid size={3}>
                        { !disabled &&
                          <Clear 
                            style={{ cursor: "pointer" }} 
                            onClick={() => {
                              char.body.concentrating_on = null;
                              // setChangeTypes(["Concentration"]);
                              setChanging(true);
                            }} 
                          />
                        }
                      </Grid>
                    </Grid> 
                  }
                  <DandDButtonBox name="Use Anyway"
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={() => {
                      triggerAbilityEffect("Slots");
                    }} 
                  />
                </div>
              );
            }
          } else {
            if (true) {
              // if (obj.name.includes("Cultists")) {
              //   debugger;
              // }
              const use_string = obj.use_string({amount_consumed, obj: char, action});
              return (
                <DandDButtonBox name={use_string}
                  disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                  onClick={() => {
                    if (obj.concentration) {
                      char.body.concentrating_on = obj;
                    }
                    triggerAbilityEffect("Special");
                  }} 
                />
              );
            }
          }
        } else if (resource_consumed === "None") {
          if (obj.concentration && char.body.concentrating_on) {
            if (simple) {
              return (
                <>
                  <DandDButtonBox name={obj.use_string({amount_consumed, obj: char, action})}
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      setPopoverAnchorEl(event.currentTarget);
                    }} 
                  />
                  { renderExtras() }
                </>
              );
            } else {
              return (
                <div>You're already concentrating on
                  { char.body.concentrating_on &&
                    <Grid container spacing={0} direction="row">
                      <Grid size={9}>
                        <ViewSpell spell={char.body.concentrating_on} show_ritual />
                      </Grid>
                      <Grid size={3}>
                        { !disabled &&
                          <Clear 
                            style={{ cursor: "pointer" }} 
                            onClick={() => {
                              char.body.concentrating_on = null;
                              // setChangeTypes(["Concentration"]);
                              setChanging(true);
                            }} 
                          />
                        }
                      </Grid>
                    </Grid> 
                  }
                  <DandDButtonBox name="Use Anyway"
                    disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                    onClick={() => {
                      triggerAbilityEffect("Slots");
                    }} 
                  />
                </div>
              );
            }
          } else {
            return (
              <DandDButtonBox name={obj.use_string({amount_consumed, obj: char, action})}
                disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                onClick={() => {
                  triggerAbilityEffect("Slots");
                }} 
              />
            );
          }
        } else if (resource_consumed === "Recharge") {
          if (the_ability instanceof Ability || the_ability instanceof CreatureAbility) {
            const str = obj.use_string({ amount_consumed: 0, obj: char, action });
            if (str === "Ready") {
              return (
                <DandDButtonBox name={str}
                  disabled={ disabled }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    triggerAbilityEffect("Recharge");
                  }} 
                />
              );
            } else {
              return (
                <DandDButtonBox name={str}
                  disabled={ disabled }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    // Do a recharge check
                    const rolls = new RollPlus();
                    rolls.type = "Recharge";
                    rolls.count = 1;
                    rolls.size = the_ability.consumed_resource.recharge_die;
                    const new_roll = char_util.rollOther({ rolls: [rolls], type: "Recharge" });
                    setRoll(new_roll);
                    setPopoverTime(common_util.get_time_number());
                    if (new_roll.length === 1 && new_roll[0].roll_values.length === 1 && new_roll[0].roll_values[0].main_values.length === 1 && the_ability.consumed_resource.recharge_on.includes(new_roll[0].roll_values[0].main_values[0])) {
                      char.body.resource_holder.setSpecialResourceUsed(obj.true_id, 0);
                      // setChangeTypes(["hp"]);
                      setChanging(true);
                    }
                  }} 
                />
              );
            }
          }
        } else {
          // Get the resources
          const resource = char.body.resources.find(o => o.type_id === resource_consumed);
          
          // if (obj.name.includes("Wrathful Smite")) {
          //   debugger;
          // }
          if (resource) {
            if (obj.concentration && char.body.concentrating_on) {
              if (simple) {
                return (
                  <>
                    <DandDButtonBox name={obj.use_string({amount_consumed, obj: char, action})}
                      disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        setPopoverAnchorEl(event.currentTarget);
                      }} 
                    />
                    { renderExtras() }
                  </>
                );
              } else {
                return (
                  <div>You're already concentrating on
                    { char.body.concentrating_on &&
                      <Grid container spacing={0} direction="row">
                        <Grid size={9}>
                          <ViewSpell spell={char.body.concentrating_on} show_ritual />
                        </Grid>
                        <Grid size={3}>
                          { !disabled &&
                            <Clear 
                              style={{ cursor: "pointer" }} 
                              onClick={() => {
                                char.body.concentrating_on = null;
                                // setChangeTypes(["Concentration"]);
                                setChanging(true);
                              }} 
                            />
                          }
                        </Grid>
                      </Grid> 
                    }
                    <DandDButtonBox name="Use Anyway"
                      disabled={ disabled } // obj.disabled({char: char, action, level: level2}) }
                      onClick={() => {
                        triggerAbilityEffect("Slots");
                      }} 
                    />
                  </div>
                );
              }
            } else {
              return (
                <DandDButtonBox name={obj.use_string({ amount_consumed, obj: char, action })}
                  disabled={ disabled }
                  onClick={() => {
                    triggerAbilityEffect(resource_consumed);
                  }} 
                />
              );
            }
          }
        }
      }
    }
  } else if (obj instanceof CharacterExtraEffects) {
    if (log_it) {
      debugger;
    }
    const main_cf = obj.main_cf;
        
    if (main_cf) {
      let { resource_consumed, amount_consumed, slot, disabled: action_disabled } = char_util.getResourceStuff({ char: char, action, obj, log_it });
      const disabled = disabled_inherited || action_disabled;
        
      if (slot) {
        if (resource_consumed !== "Pact") {
          resource_consumed = "Slot";
        }
      }
      if (resource_consumed === "Slot") {
        return (
          <>
            <DandDButtonBox name={ `Use Level ${obj.slot_level} Slot` }
              // log_it={obj.name === "Misty Hand"}
              disabled={ disabled } // obj.disabled(char, level2) }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                triggerAbilityEffect("Slots");
              }} 
            />
            { renderExtras() }
          </>
        );
      } else if (resource_consumed === "Pact") {
        return (
          <>
            <DandDButtonBox name={ `Use Pact Slot` }
              // log_it={obj.name === "Misty Hand"}
              disabled={ disabled } // obj.disabled(char, level2) }
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                triggerAbilityEffect("Pact");
              }} 
            />
            { renderExtras() }
          </>
        );
      } else if (resource_consumed === "Charge" && char instanceof Character) {
        // obj.source_id; // This is the true_id of the CharacterItem that grants this ability
        const item_id = obj.use_item_id;
        const item_finder = char.inventory.items.filter(o => o.true_id === item_id);
        if (item_finder.length === 1) {
          const char_item = item_finder[0];
          if ((char_item.item_type === "Potion" || char_item.item_type === "Scroll" || char_item.item_type === "Dust")) {
            // if (obj.name.includes("Holy Water")) {
            //   debugger;
            // }
            return (
              <DandDButtonBox name={ `${char_item.count} in Inventory` }
                disabled={ disabled } // char_item.count <= 0 }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  triggerAbilityEffect("Charge");
                }} 
              />
            );
          } else {
            return (
              <DandDButtonBox name={ `${amount_consumed} Charge${amount_consumed > 1 ? "s" : ""}|${char_item.charges_used}/${char_item.max_charges}` }
                disabled={ disabled } // (char_item.charges_used + amount_consumed) > char_item.max_charges }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  triggerAbilityEffect("Charge");
                }} 
              />
            );
          }
        }
      } else if (resource_consumed === "Special") {
        const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(obj.true_id);
        const use_string = `${special_resource_used}/${obj.special_resource_amount}`;
        // debugger;
        return (
          <DandDButtonBox name={use_string}
            disabled={ disabled } // obj.disabled(char, level2) }
            onClick={() => {
              triggerAbilityEffect("Special");
            }} 
          />
        );
      } else {
        // Get the resources
        const resource = char.body.resources.find(o => o.type_id === resource_consumed);
        
        if (resource) {
          const use_string = `${amount_consumed} ${amount_consumed > 1 ? resource.name : resource.use_singular}|${resource.used}/${resource.total}`;
          return (
            <DandDButtonBox name={use_string}
              disabled={ disabled } // obj.disabled(char, level2) }
              onClick={() => {
                triggerAbilityEffect(resource_consumed);
              }} 
            />
          );
        }
      }
    }
  } else {
    // if (action.name.includes("Cure")) {
    //   console.log(change_types);
    //   console.log(changing);
    // }
    // if (obj instanceof StoredSpell) {
    //   debugger;
    // }
    if (obj instanceof StoredSpell) {
      if (obj.concentration && char.body.concentrating_on) {
        return (
          <>
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox name={ obj.castings === 1 ? (obj.has_been_cast ? "Stored Spell has been Cast" : `Cast Stored Spell`) : `${obj.has_been_cast}/${obj.castings} Castings of Stored Spell` }
                  disabled={ disabled_inherited || obj.has_been_cast >= obj.castings }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    setPopoverAnchorEl(event.currentTarget);
                  }} 
                />
              </Grid>
              { obj.has_been_cast >= obj.castings && char instanceof Character && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox name="Remove Stored Spell"
                    disabled={ disabled_inherited } // obj.disabled({char: char, action, level: level2}) }
                    onClick={() => {
                      const char_item = char.inventory.items.find(o => o.true_id === obj.item_id);
                      if (char_item) {
                        if (char.body.concentrating_on instanceof StoredSpell && char.body.concentrating_on.true_id === obj.true_id) {
                          char.body.concentrating_on = null;
                        }
                        if (char_item.spell_storing_spell.true_id === obj.true_id) {
                          char_item.spell_storing_spell = new StoredSpell();
                        } else {
                          char_item.stored_spells = char_item.stored_spells.filter(o => o.true_id !== obj.true_id);
                        }
                        char.changed();
                        char.subscription_holder.itemsChanged();
                        char.subscription_holder.recalculate();
                      }
                    }} 
                  />
                </Grid>
              }
            </Grid>
            { renderExtras() }
          </>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox name={ obj.castings === 1 ? (obj.has_been_cast ? "Stored Spell has been Cast" : `Cast Stored Spell`) : `${obj.has_been_cast}/${obj.castings} Castings of Stored Spell` }
                disabled={ disabled_inherited || obj.has_been_cast >= obj.castings } // obj.disabled({char: char, action, level: level2}) }
                onClick={() => {
                  triggerAbilityEffect("Stored Spell");
                }} 
              />
            </Grid>
            { obj.has_been_cast >= obj.castings && char instanceof Character && 
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox name="Remove Stored Spell"
                  disabled={ disabled_inherited } // obj.disabled({char: char, action, level: level2}) }
                  onClick={() => {
                    const char_item = char.inventory.items.find(o => o.true_id === obj.item_id);
                    if (char_item) {
                      if (char_item.spell_storing_spell.true_id === obj.true_id) {
                        char_item.spell_storing_spell = new StoredSpell();
                      } else {
                        char_item.stored_spells = char_item.stored_spells.filter(o => o.true_id !== obj.true_id);
                      }
                      char.changed();
                      char.subscription_holder.itemsChanged();
                      char.subscription_holder.recalculate();
                    }
                  }} 
                />
              </Grid>
            }
          </Grid>
        );
      }
    } else {
      const { disabled: action_disabled } = char_util.getResourceStuff({ char: char, action, obj });
      const disabled = disabled_inherited || action_disabled;
        
      const spell = obj;
      // const self_condition_ids = spell.self_condition();
      if (action.ritual_only || ritual) {
        return (
          <DandDButtonBox 
            disabled={true} 
            name="Ritual" 
            onClick={() => {
              triggerAbilityEffect("Slots");
            }}
          />
        );
      } else if (spell.level === 0 || (spell.at_will && level2 === spell.level)) {
        if (spell.spell && spell.spell.concentration) {
          if (char.body.concentrating_on) {
            return (
              <>
                <DandDButtonBox name="At Will"
                  disabled={ disabled }
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    setPopoverAnchorEl(event.currentTarget);
                  }} 
                />
                { renderExtras() }
              </>
            );
          } else {
            return (
              <DandDButtonBox name="At Will"
                disabled={ disabled }
                onClick={() => {
                  triggerAbilityEffect("Slots");
                }} 
              />
            );
          }
        } else {
          return (
            <DandDButtonBox 
              disabled={disabled}
              name="At Will"
              onClick={() => {
                triggerAbilityEffect("Slots");
              }}
            />
          );
        }
      } else {
        // Get the slots for the level
        const slots = char.body.use_slots.filter(o => o.level === level2);
        if (slots.length === 1) {
          const slot = slots[0];
          if (spell.spell && spell.spell.concentration) { 
            if (char.body.concentrating_on) {
              return (
                <>
                  <DandDButtonBox name={ `${spell.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                    disabled={ disabled || slot.used === slot.total }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      setPopoverAnchorEl(event.currentTarget);
                    }} 
                  />
                  { renderExtras() }
                </>
              );
            } else {
              return (
                <DandDButtonBox name={ `${spell.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                  disabled={ disabled || slot.used === slot.total }
                  onClick={() => {
                    triggerAbilityEffect(slot.type_id);
                  }} 
                />
              );
            }
          } else {
            return (
              <DandDButtonBox name={ `${spell.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                disabled={ disabled || slot.used === slot.total }
                onClick={() => {
                  triggerAbilityEffect(slot.type_id);
                }} 
              />
            );
          }
        } else {
          return (
            <>
              <DandDButtonBox name={ `${spell.level < level2 ? "Upc" : "C"}ast at Level ${level2}` }
                disabled={ disabled } // slots.filter(o => o.used < o.total).length === 0 }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  setPopoverAnchorEl(event.currentTarget);
                }} 
              />
              { renderExtras() }
            </>
          );
        }
      }
    }
  }
  return <></>;
}
